import React, { useState } from "react";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lock, X, ShieldCheck } from "lucide-react";

export function ChangePasswordDialog({ forced = false, onClose }) {
    const { setUser } = useAuth();
    const [cur, setCur] = useState("");
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (pw.length < 8) return setErr("New password must be at least 8 characters");
        if (pw !== confirm) return setErr("New passwords do not match");
        setBusy(true);
        try {
            await api.post("/auth/change-password", { current_password: cur, new_password: pw });
            try { const { data } = await api.get("/auth/me"); setUser(data); } catch { /* ignore */ }
            toast.success("Password updated");
            onClose?.(true);
        } catch (e2) { setErr(fmtErr(e2.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" data-testid="change-password-dialog">
            <div className="bg-white rounded-md shadow-2xl w-[440px] max-w-full">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
                    <ShieldCheck className="w-4 h-4 text-brand" />
                    <span className="font-display font-semibold text-sm">{forced ? "Set a new password to continue" : "Change password"}</span>
                    {!forced && <button onClick={() => onClose?.(false)} className="ml-auto text-muted-foreground hover:text-foreground" data-testid="change-password-close"><X className="w-4 h-4" /></button>}
                </div>
                <form onSubmit={submit} className="p-4 space-y-3" data-testid="change-password-form">
                    {forced && <p className="text-xs text-muted-foreground">For security, please replace the temporary password you were given with one only you know.</p>}
                    <Field label="Current password" value={cur} onChange={setCur} testid="cp-current" />
                    <Field label="New password" value={pw} onChange={setPw} testid="cp-new" />
                    <Field label="Confirm new password" value={confirm} onChange={setConfirm} testid="cp-confirm" />
                    {err && <div className="text-xs text-destructive bg-destructive/10 border border-destructive/20 px-3 py-2 rounded-sm" data-testid="cp-error">{err}</div>}
                    <button type="submit" disabled={busy} data-testid="cp-submit" className="w-full bg-brand hover:bg-brand-hover text-white font-display font-semibold text-sm py-2.5 rounded-sm disabled:opacity-50">{busy ? "Saving…" : "Update password"}</button>
                </form>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, testid }) {
    return (
        <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-display block mb-1.5">{label}</label>
            <div className="flex items-center border border-input rounded-sm bg-white focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20">
                <Lock className="w-4 h-4 text-muted-foreground mx-3" />
                <input type="password" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} required className="flex-1 py-2.5 pr-3 bg-transparent outline-none text-sm" />
            </div>
        </div>
    );
}
